import { useEffect, useState } from "react";
import { api } from "./api";
import { ISLANDS as SEED_ISLANDS } from "./constants";

// Cache-in-module so multiple hooks share the same fetch across the app.
let cachedIslands = null;
let inflight = null;

/**
 * Fetches the active island list from `/api/islands` and normalizes it into
 * the same shape the frontend already consumes: `{ slug, name, flag, color, ...raw }`.
 * On first render (before the fetch resolves) we return the compile-time
 * seed so the UI never flashes empty.
 */
export const useIslands = () => {
  const [islands, setIslands] = useState(cachedIslands || SEED_ISLANDS);
  const [loading, setLoading] = useState(!cachedIslands);

  useEffect(() => {
    if (cachedIslands) return;
    if (!inflight) {
      inflight = api
        .get("/islands")
        .then((r) => {
          const remote = (r.data || []).map((d) => ({
            slug: d.slug,
            name: d.name,
            flag: d.flag_emoji,
            color: d.accent_color,
            region: d.region,
            meta_title: d.meta_title,
            meta_description: d.meta_description,
          }));
          // Prepend "All Islands" — it's a UI-only element, not a DB record.
          const withAll = [
            { slug: "all", name: "All Islands", flag: "🌍", color: "#0AADA0" },
            ...remote,
          ];
          cachedIslands = withAll;
          return withAll;
        })
        .catch(() => SEED_ISLANDS)
        .finally(() => { /* keep inflight so retries stay coalesced */ });
    }
    let cancelled = false;
    inflight.then((data) => {
      if (!cancelled) {
        setIslands(data);
        setLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, []);

  return { islands, loading };
};

export const islandBySlug = (islands, slug) =>
  islands.find((i) => i.slug === slug) || null;
